import { Button, CircularProgress, makeStyles, Theme, Typography } from '@material-ui/core';
import React, { useState, useEffect, useMemo } from 'react';
import { useSnackbar } from 'notistack';
import axios from 'axios';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

import { member as v_member, basic, Values, product as v_product, SNACKBAR_TIME } from '../../../request/values';
import { COLORS } from '../../../shared/Theme';
import { DialogConfirm } from '../../../components/Dialog';
import ToastStr from '../../../request/toastStr';
import TitleBar from '../../../shared/header/TitleBar';
import { formatMoneyAmount, getTranslatedLicenseName } from '../../../locales/formatModule';

const SERVER = basic.server;

const useStyles = makeStyles((theme: Theme) => ({
	page: {
		maxWidth: 720,
		margin: '0 auto',
		height: '100%',
		boxSizing: 'border-box',
		backgroundColor: COLORS.WHITE,
		display: 'flex',
		flexDirection: 'column',
		overflow: 'hidden',
		padding: '10px 3vw 0',
		[theme.breakpoints.up('md')]: {
			padding: '50px 0 0',
		},
	},
	scrollBody: {
		flex: 1,
		minHeight: 0,
		overflowY: 'auto',
		WebkitOverflowScrolling: 'touch',
		paddingBottom: 40,
	},
	catalogRow: {
		display: 'flex',
		flexWrap: 'nowrap',
		alignItems: 'stretch',
		gap: 10,
		marginBottom: 14,
		padding: '12px 10px',
		borderRadius: 12,
		background: COLORS.WHITE,
		border: '1px solid #E5EAF0',
		overflowX: 'auto',
		WebkitOverflowScrolling: 'touch',
		scrollbarWidth: 'thin',
	},
	catalogItem: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		gap: 4,
		flex: '0 0 auto',
		width: 64,
	},
	catalogThumb: {
		width: 56,
		height: 56,
		borderRadius: 12,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		overflow: 'hidden',
		background: COLORS.WHITE,
		border: '1px solid #E5EAF0',
		'& img': {
			maxWidth: '80%',
			maxHeight: '80%',
			objectFit: 'contain',
		},
	},
	catalogThumbOff: {
		borderColor: '#E5EAF0',
		'& img': {
			opacity: 0.45,
			filter: 'grayscale(1)',
		},
	},
	catalogThumbOn: {
		borderColor: COLORS.WATER_BLUE,
	},
	catalogName: {
		fontFamily: 'NanumB',
		fontWeight: 700,
		fontSize: 10,
		color: COLORS.CHARCOAL_GREY,
		textAlign: 'center',
		lineHeight: 1.2,
		maxWidth: '100%',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	catalogCount: {
		fontFamily: 'NanumB',
		fontWeight: 800,
		fontSize: 11,
		lineHeight: 1,
	},
	catalogCountOff: {
		color: '#A0AAB4',
	},
	catalogCountOn: {
		color: COLORS.WATER_BLUE,
	},
	section: {
		borderRadius: 14,
		border: '1px solid #E5EAF0',
		background: COLORS.WHITE,
		padding: 12,
		marginBottom: 16,
		[theme.breakpoints.up('md')]: {
			padding: 16,
		},
		'&:last-of-type': {
			marginBottom: 0,
		},
	},
	sectionHeader: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		marginBottom: 10,
		gap: 8,
	},
	sectionTitle: {
		fontFamily: 'NanumB',
		fontWeight: 800,
		fontSize: '0.98rem',
		color: COLORS.CHARCOAL_GREY,
	},
	sectionBadge: {
		flexShrink: 0,
		borderRadius: 999,
		padding: '3px 9px',
		fontFamily: 'NanumB',
		fontWeight: 800,
		fontSize: 11,
		background: '#EAF6FC',
		color: COLORS.WATER_BLUE,
	},
	sectionBadgePending: {
		background: '#FFF4E5',
		color: '#C77700',
	},
	cardList: {
		display: 'flex',
		flexDirection: 'column',
		gap: 10,
	},
	card: {
		display: 'flex',
		flexDirection: 'column',
		gap: 10,
		padding: 12,
		borderRadius: 12,
		border: '1px solid #E5EAF0',
		background: COLORS.WHITE,
	},
	cardPending: {
		borderColor: '#F0D9A8',
		borderStyle: 'dashed',
	},
	cardTop: {
		display: 'flex',
		alignItems: 'center',
		gap: 12,
	},
	imgBox: {
		flexShrink: 0,
		width: 72,
		height: 72,
		borderRadius: 12,
		background: COLORS.WHITE,
		border: '1px solid #E5EAF0',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		overflow: 'hidden',
		'& img': {
			maxWidth: '85%',
			maxHeight: '85%',
			objectFit: 'contain',
		},
	},
	info: {
		flex: 1,
		minWidth: 0,
		display: 'flex',
		flexDirection: 'column',
		gap: 6,
	},
	nameRow: {
		display: 'flex',
		alignItems: 'flex-start',
		justifyContent: 'space-between',
		gap: 8,
	},
	name: {
		flex: 1,
		minWidth: 0,
		fontFamily: 'NanumB',
		fontWeight: 800,
		fontSize: '1rem',
		color: COLORS.WATER_BLUE,
		lineHeight: 1.3,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	level: {
		flexShrink: 0,
		borderRadius: 10,
		padding: '3px 8px',
		background: '#C2E9FF',
		color: COLORS.WATER_BLUE,
		fontFamily: 'NanumB',
		fontWeight: 800,
		fontSize: 12,
		lineHeight: 1.2,
	},
	maker: {
		fontFamily: 'NanumB',
		fontWeight: 700,
		fontSize: 12,
		color: '#90A4B8',
	},
	metrics: {
		display: 'grid',
		gridTemplateColumns: '1fr 1fr',
		gap: 8,
	},
	metric: {
		borderRadius: 10,
		padding: '8px 10px',
		background: COLORS.WHITE,
		border: '1px solid #E5EAF0',
	},
	metricLabel: {
		fontFamily: 'NanumB',
		fontWeight: 700,
		fontSize: 11,
		color: '#90A4B8',
		marginBottom: 2,
	},
	metricValue: {
		fontFamily: 'NanumB',
		fontWeight: 800,
		fontSize: 14,
		color: COLORS.WATER_BLUE,
	},
	metricValueProfit: {
		color: '#1AB82D',
	},
	actionBtn: {
		width: '100%',
		border: 'none',
		borderRadius: 10,
		padding: '10px 12px',
		fontFamily: 'NanumB',
		fontWeight: 800,
		fontSize: 14,
		color: COLORS.WHITE,
		backgroundColor: COLORS.WATER_BLUE,
		cursor: 'pointer',
	},
	actionBtnPending: {
		backgroundColor: '#B8C0C8',
		color: COLORS.WHITE,
		border: 'none',
		cursor: 'default',
	},
	emptyBox: {
		textAlign: 'center',
		padding: '18px 8px',
		color: '#90A4B8',
		fontFamily: 'NanumB',
		fontWeight: 700,
		fontSize: 13,
	},
	emptyPage: {
		textAlign: 'center',
		padding: '40px 12px',
		'& img': {
			width: 120,
			opacity: 0.4,
			marginBottom: 12,
		},
	},
	emptyTitle: {
		fontFamily: 'NanumB',
		fontWeight: 800,
		fontSize: '1.05rem',
		color: COLORS.DOWN,
		marginBottom: 8,
	},
	emptyCta: {
		marginTop: 12,
		borderRadius: 10,
		backgroundColor: COLORS.WATER_BLUE,
		color: COLORS.WHITE,
		fontFamily: 'NanumB',
		fontWeight: 700,
		textTransform: 'none',
		padding: '8px 16px',
		'&:hover': {
			backgroundColor: '#0f7bbd',
		},
	},
	statusPage: {
		minHeight: 240,
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		gap: 12,
		padding: '40px 12px',
		textAlign: 'center',
		color: '#6B7785',
	},
}));

interface iProductList {
	product_num: number;
	license_name: string;
	license_image?: string;
	product_image?: string;
	sale_company_name: string;
	product_sale_app_num: number;
	decompose_status: string | null;
	license_level: number;
	product_price: number;
	company_num: number;
	material_price?: number;
	expected_reward?: number;
}

interface iCatalogItem {
	license_num: number;
	license_name: string;
	license_image: string;
	license_level: number;
	decompose_count: number;
}

type iDialog = {
	open: boolean;
	type: string;
	index: number;
};

const ProductPurchaseList: React.FC<any> = (props) => {
	const classes = useStyles();
	const { enqueueSnackbar } = useSnackbar();
	const history = useHistory();
	const { member } = props;
	const { t } = useTranslation('student');
	const { t: tObject } = useTranslation('object');

	const [product, setProduct] = useState<iProductList[]>([]);
	const [catalog, setCatalog] = useState<iCatalogItem[]>([]);
	const [loading, setLoading] = useState(true);
	const [loadError, setLoadError] = useState(false);
	const [decomposing, setDecomposing] = useState(false);
	const [dialog, setDialog] = useState<iDialog>({
		open: false,
		type: 'approval',
		index: 0,
	});

	const readyProducts = useMemo(() => product.filter((item) => item.decompose_status !== 'ing'), [product]);
	const pendingProducts = useMemo(() => product.filter((item) => item.decompose_status === 'ing'), [product]);

	const handleClose = () => {
		setDialog((prevState: any) => Object.assign({}, prevState, { open: false }));
	};

	const handleApprovalOpen = (index: number) => {
		if (index >= 0) {
			setDialog((prevState: any) =>
				Object.assign({}, prevState, {
					open: true,
					type: 'approval',
					index,
				}),
			);
		}
	};

	const getProductPurchaseList = () => {
		setLoading(true);
		setLoadError(false);
		const url = new URL(basic.server + v_member.member + v_product.product_list);
		url.search = new URLSearchParams({
			companyNum: member.companyNum,
			classVersion: member.classVersion,
		}).toString();

		return axios({
			method: 'GET',
			withCredentials: true,
			url: url.toString(),
		})
			.then((res) => res.data)
			.then((data: any) => {
				if (data.code === Values.SUCCESS_CODE) {
					setCatalog(Array.isArray(data.catalog) ? data.catalog : []);
					if (!data.appList || data.appList.length === 0) {
						setProduct([]);
					} else {
						setProduct(data.appList);
					}
				} else if (data.code === Values.FAIL_CODE) {
					setLoadError(true);
					enqueueSnackbar(ToastStr.REQUEST_GET_POST_FAIL_STR, {
						variant: 'warning',
						autoHideDuration: SNACKBAR_TIME,
					});
				} else {
					setLoadError(true);
					enqueueSnackbar(ToastStr.REQUEST_GET_POST_FAIL_STR, {
						variant: 'error',
						autoHideDuration: SNACKBAR_TIME,
					});
				}
			})
			.catch(() => {
				setLoadError(true);
				enqueueSnackbar(ToastStr.REQUEST_GET_POST_FAIL_STR, {
					variant: 'error',
					autoHideDuration: SNACKBAR_TIME,
				});
			})
			.finally(() => setLoading(false));
	};

	const postProductDecompose = (productSaleAppNum: number) => {
		if (decomposing) return;
		setDecomposing(true);
		return axios({
			method: 'POST',
			data: {
				product_sale_app_num: productSaleAppNum,
			},
			withCredentials: true,
			url: SERVER + v_member.member + v_product.product_decompose,
		})
			.then((res) => res.data)
			.then((data: any) => {
				if (data.code === Values.SUCCESS_CODE) {
					enqueueSnackbar(ToastStr.PRODUCT_DECOMPOSE_SUCCESS_STR, {
						variant: 'success',
						autoHideDuration: SNACKBAR_TIME,
					});
				} else if (data.code === Values.PRODUCT_DECOMPOSE_ING_CODE) {
					enqueueSnackbar(ToastStr.PRODUCT_DECOMPOSE_PROCEEDING_STR, {
						variant: 'warning',
						autoHideDuration: SNACKBAR_TIME,
					});
				} else if (data.code === Values.FAIL_CODE) {
					enqueueSnackbar(ToastStr.PRODUCT_DECOMPOSE_FAIL_STR, {
						variant: 'warning',
						autoHideDuration: SNACKBAR_TIME,
					});
				} else {
					enqueueSnackbar(ToastStr.REQUEST_GET_POST_FAIL_STR, {
						variant: 'error',
						autoHideDuration: SNACKBAR_TIME,
					});
				}
				handleClose();
				return getProductPurchaseList();
			})
			.catch(() => {
				enqueueSnackbar(ToastStr.REQUEST_GET_POST_FAIL_STR, {
					variant: 'error',
					autoHideDuration: SNACKBAR_TIME,
				});
			})
			.finally(() => setDecomposing(false));
	};

	useEffect(() => {
		getProductPurchaseList();
	}, []);

	const goPurchase = () => history.push('/ProductPurchase');

	const dialogProduct = product[dialog.index];

	return (
		<div className={classes.page}>
			<TitleBar history={history} title={t('ProductPurchaseList.pageTitle')} titleIcon="cart" />

			<div className={classes.scrollBody}>
				{loading ? (
					<div className={classes.statusPage} data-testid="dist-owned-product-loading">
						<CircularProgress size={32} />
						<Typography>{t('ProductPurchaseList.loading')}</Typography>
					</div>
				) : loadError ? (
					<div className={classes.statusPage} data-testid="dist-owned-product-error">
						<Typography>{t('ProductPurchaseList.loadError')}</Typography>
						<Button className={classes.emptyCta} onClick={getProductPurchaseList}>
							{t('ProductPurchaseList.retry')}
						</Button>
					</div>
				) : (
					<>
						{catalog.length > 0 && (
							<div className={classes.catalogRow}>
								{catalog.map((item) => {
									const done = Number(item.decompose_count) > 0;
									const name = getTranslatedLicenseName(tObject, item.license_name);
									return (
										<div key={item.license_num || item.license_level} className={classes.catalogItem}>
											<div className={clsx(classes.catalogThumb, done ? classes.catalogThumbOn : classes.catalogThumbOff)}>
												<img src={basic.server + '/displayImage?fileName=' + item.license_image} alt={name} />
											</div>
											<div className={classes.catalogName}>{name}</div>
											<div className={clsx(classes.catalogCount, done ? classes.catalogCountOn : classes.catalogCountOff)}>
												{done ? t('ProductPurchaseList.decomposeCount', { n: item.decompose_count }) : t('ProductPurchaseList.decomposeNone')}
											</div>
										</div>
									);
								})}
							</div>
						)}

						{product.length === 0 ? (
							<div className={classes.emptyPage} data-testid="dist-owned-product-empty">
								<img src="/images/title/cart.webp" alt="" />
								<Typography className={classes.emptyTitle}>{t('ProductPurchaseList.empty')}</Typography>
								<Button className={classes.emptyCta} onClick={goPurchase}>
									{t('ProductPurchaseList.emptyCta')}
								</Button>
							</div>
						) : (
							<>
								<section className={classes.section}>
									<div className={classes.sectionHeader}>
										<Typography className={classes.sectionTitle}>{t('ProductPurchaseList.readyTitle')}</Typography>
										<span className={classes.sectionBadge}>
											{readyProducts.length}
											{t('ProductPurchaseList.countSuffix')}
										</span>
									</div>
									<div className={classes.cardList}>
										{readyProducts.length === 0 ? (
											<div className={classes.emptyBox}>{t('ProductPurchaseList.readyEmpty')}</div>
										) : (
											readyProducts.map((item) => {
												const originalIndex = product.findIndex((p) => p.product_sale_app_num === item.product_sale_app_num);
												return (
													<OwnedProductCard
														key={item.product_sale_app_num}
														item={item}
														pending={false}
														onRequest={() => handleApprovalOpen(originalIndex)}
													/>
												);
											})
										)}
									</div>
								</section>

								<section className={classes.section}>
									<div className={classes.sectionHeader}>
										<Typography className={classes.sectionTitle}>{t('ProductPurchaseList.pendingTitle')}</Typography>
										<span className={clsx(classes.sectionBadge, classes.sectionBadgePending)}>
											{pendingProducts.length}
											{t('ProductPurchaseList.countSuffix')}
										</span>
									</div>
									<div className={classes.cardList}>
										{pendingProducts.length === 0 ? (
											<div className={classes.emptyBox}>{t('ProductPurchaseList.pendingEmpty')}</div>
										) : (
											pendingProducts.map((item) => (
												<OwnedProductCard key={item.product_sale_app_num} item={item} pending={true} onRequest={() => undefined} />
											))
										)}
									</div>
								</section>
							</>
						)}
					</>
				)}
			</div>

			<DialogConfirm
				open={dialog.open}
				handleClose={handleClose}
				title={t('ProductPurchaseList.pageTitle')}
				context={
					dialogProduct &&
					`${t('ProductPurchaseList.dialogTitle')}\n${t('ProductPurchaseList.dialogContextPrefix')}${getTranslatedLicenseName(tObject, dialogProduct.license_name)}`
				}
				yesConfirm={() => dialogProduct && postProductDecompose(dialogProduct.product_sale_app_num)}
				noConfirm={handleClose}
				confirmDisabled={decomposing}
			/>
		</div>
	);
};

const OwnedProductCard: React.FC<{
	item: iProductList;
	pending: boolean;
	onRequest: () => void;
}> = ({ item, pending, onRequest }) => {
	const classes = useStyles();
	const { t } = useTranslation('student');
	const { t: tObject } = useTranslation('object');
	const productName = getTranslatedLicenseName(tObject, item.license_name);
	const imageName = item.license_image || item.product_image;
	const materialValue = Math.max(0, Math.floor(Number(item.material_price) || 0));
	const serverExpectedReward = Number(item.expected_reward);
	const expectedReward = Math.max(
		0,
		Math.floor(Number.isFinite(serverExpectedReward) ? serverExpectedReward : Math.max((Number(item.product_price) || 0) - materialValue, 0)),
	);

	return (
		<div
			className={clsx(classes.card, pending && classes.cardPending)}
			data-testid={`dist-owned-product-item-${item.product_sale_app_num}`}
			data-product-state={pending ? 'pending' : 'ready'}
			data-product-sale-app-num={item.product_sale_app_num}
			data-company-num={item.company_num}
			data-product-num={item.product_num}
		>
			<div className={classes.cardTop}>
				<div className={classes.imgBox}>
					<img src={basic.server + '/displayImage?fileName=' + imageName} alt={productName} />
				</div>
				<div className={classes.info}>
					<div className={classes.nameRow}>
						<Typography className={classes.name}>{productName}</Typography>
						<span className={classes.level}>{`Lv.${item.license_level}`}</span>
					</div>
					<div className={classes.maker}>{t('ProductPurchaseList.manufacturerPrefix') + item.sale_company_name}</div>
				</div>
			</div>

			<div className={classes.metrics}>
				<div className={classes.metric}>
					<div className={classes.metricLabel}>{t('ProductPurchaseList.materialValue')}</div>
					<div className={classes.metricValue}>{formatMoneyAmount(materialValue)}</div>
				</div>
				<div className={classes.metric}>
					<div className={classes.metricLabel}>{t('ProductPurchaseList.expectedProfit')}</div>
					<div className={clsx(classes.metricValue, classes.metricValueProfit)}>{formatMoneyAmount(expectedReward)}</div>
				</div>
			</div>

			<button
				className={clsx(classes.actionBtn, pending && classes.actionBtnPending)}
				onClick={onRequest}
				disabled={pending}
				data-testid={`dist-owned-product-request-recycle-btn-${item.product_sale_app_num}`}
			>
				{pending ? t('ProductPurchaseList.waitingApproval') : t('ProductPurchaseList.requestRecycle')}
			</button>
		</div>
	);
};

export default ProductPurchaseList;
